import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Icon } from '../icons';
import Select from './Select';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Stato di paginazione client-side: pagina + dimensione pagina, con reset
// automatico a pagina 1 quando cambiano i filtri (deps) a monte.
export function usePagination(total, deps, initialPageSize = 10) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, deps);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const clampedPage = Math.min(page, totalPages);
  const start = total === 0 ? 0 : (clampedPage - 1) * pageSize;
  const end = Math.min(start + pageSize, total);

  return {
    page: clampedPage, pageSize, totalPages, start, end,
    setPage,
    setPageSize: (n) => { setPageSize(n); setPage(1); },
  };
}

// Calcola quante righe entrano nello spazio verticale visibile (tra il top
// della card e il fondo della viewport) e imposta la pageSize di conseguenza,
// cosi' la tabella non forza mai lo scroll della pagina: se ci sono piu'
// risultati di quanti ne entrano, li si sfoglia con la paginazione. La card
// riceve un max-height (non un height fisso): se le righe sono poche la
// card si restringe al contenuto e la paginazione resta subito sotto
// l'ultima riga invece di restare ancorata in fondo allo spazio disponibile.
export function useFitPageSize({ cardRef, wrapRef, theadRef, rowHeight = 44, minRows = 3, bottomGap = 32 }, currentPageSize, setPageSize, ready) {
  useLayoutEffect(() => {
    if (!ready) return undefined;
    let frame;
    let timeout;
    const recompute = () => {
      const card = cardRef.current;
      const wrap = wrapRef.current;
      if (!card || !wrap) return;
      const top = card.getBoundingClientRect().top;
      const available = Math.max(220, window.innerHeight - top - bottomGap);
      card.style.maxHeight = `${available}px`;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const cardStyle = getComputedStyle(card);
        const paddingY = parseFloat(cardStyle.paddingTop) + parseFloat(cardStyle.paddingBottom);
        // Sottrae tutto cio' che nella card NON e' la tabella (intestazione,
        // eventuali messaggi/avvisi, paginazione), misurato dai figli diretti:
        // il calcolo resta corretto sia con testata inline che impilata, e
        // anche quando compare un avviso sopra la tabella.
        let otherH = 0;
        for (const child of card.children) {
          if (child === wrap) continue;
          const cs = getComputedStyle(child);
          otherH += child.offsetHeight + parseFloat(cs.marginTop) + parseFloat(cs.marginBottom);
        }
        const theadH = theadRef.current ? theadRef.current.getBoundingClientRect().height : 0;
        const availableForRows = available - paddingY - otherH - theadH;
        // Misura l'altezza reale di una riga (i bottoni azione la rendono
        // piu' alta della sola stima da CSS): fallback alla costante solo
        // se non c'e' ancora nessuna riga renderizzata da misurare.
        const sampleRow = wrap.querySelector('tbody tr');
        const measuredRowHeight = sampleRow ? sampleRow.getBoundingClientRect().height : rowHeight;
        const rows = Math.max(minRows, Math.floor(availableForRows / measuredRowHeight));
        if (rows !== currentPageSize) setPageSize(rows);
      });
    };
    const schedule = () => { clearTimeout(timeout); timeout = setTimeout(recompute, 120); };
    recompute();
    window.addEventListener('resize', schedule);
    // Un form che cresce o un messaggio che appare SOPRA la card ne spostano il
    // bordo superiore e cambiano lo spazio verticale utile: osserviamo il
    // contenitore scrollabile per ricalcolare anche in questi casi (non solo al
    // resize della finestra).
    let ro;
    const container = cardRef.current ? cardRef.current.closest('.content') : null;
    if (container && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(schedule);
      ro.observe(container);
    }
    return () => {
      window.removeEventListener('resize', schedule);
      if (ro) ro.disconnect();
      clearTimeout(timeout);
      cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, currentPageSize]);
}

function Pagination({ idPrefix, page, pageSize, totalPages, total, onPageChange, onPageSizeChange, itemLabel = 'risultati', showSizeSelector = true }) {
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div className="pagination">
      {showSizeSelector ? (
        <div className="pagination-size">
          <span>Visualizza</span>
          <Select
            id={`${idPrefix}-size`}
            ariaLabel="Risultati per pagina"
            value={String(pageSize)}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            options={PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
          />
          <span>{itemLabel}</span>
        </div>
      ) : null}
      <div className="pagination-info">{total === 0 ? `0 ${itemLabel}` : `${start}–${end} di ${total}`}</div>
      <div className="pagination-nav">
        <button type="button" className="icon-btn pagination-btn" disabled={page <= 1} onClick={() => onPageChange(page - 1)} aria-label="Pagina precedente">
          <Icon name="chevronRight" size={16} style={{ transform: 'rotate(180deg)' }} />
        </button>
        <span className="pagination-page">{page} / {totalPages}</span>
        <button type="button" className="icon-btn pagination-btn" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} aria-label="Pagina successiva">
          <Icon name="chevronRight" size={16} />
        </button>
      </div>
    </div>
  );
}

export default Pagination;
